// src/hooks/integrations/synapse/useSynapseUserProjects.ts
import { useEffect, useRef, useState } from 'react';

import useApi from '@hooks/useApi';

import { SynapseProject } from '@models/integrations/synapse/Synapse';

import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

interface UserProjectsResponse {
    results: SynapseProject[];
    nextPageToken?: string;
}

/**
 * Hook for fetching user's recent projects from Synapse
 */
const useSynapseUserProjects = () => {
    const [projects, setProjects] = useState<SynapseProject[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Cache api and logger so they remain stable
    const apiRef = useRef(useApi());
    const loggerRef = useRef(Logger.make('useSynapseUserProjects'));

    const fetchProjects = async () => {
        setLoading(true);
        setError(null);

        try {
            // Use the projects endpoint from Synapse API via our Endpoints service
            // This is a GET request, not a POST
            const url = Endpoints.integrations.synapse.userProjects();

            // Parameters for the GET request:
            // - sort: LAST_ACTIVITY (default) - sort by last activity
            // - sortDirection: DESC (default) - sort descending
            // - limit: 20 - limit to 20 items as specified in the requirements
            const params = {
                sort: 'LAST_ACTIVITY',
                sortDirection: 'DESC',
                limit: 20,
            };

            const response = await apiRef.current.get<UserProjectsResponse>(url, { params });

            if (response && response.results) {
                setProjects(response.results);
            } else {
                setProjects([]);
            }

            setLoading(false);
        } catch (error) {
            loggerRef.current.error('Error fetching Synapse user projects:', error);
            setError('Failed to fetch projects');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    return {
        projects,
        loading,
        error,
        refreshProjects: fetchProjects,
    };
};

export default useSynapseUserProjects;
